import { useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';

import { useHistory, useParams } from 'react-router-dom';
import Page from 'flow/components/Setting/Page';
import { routes } from 'flow/types/routes';
import RadioButton from 'flow/components/ui-components/RadioButton';
import useAppContext from 'flow/hooks/useAppContext';
import { BlockistParams } from 'flow/types/Params';
import {
    getBlockLists,
    updateCallRouting,
} from 'flow/actions/call-routing-actions';
import SettingSection from 'flow/components/Setting/SettingSection';
import ReferralNumberCombobox, {
    ReferralNumberOption,
} from 'flow/components/Setting/components/ReferralNumberCombobox';
import { isValidPhoneNumber } from 'libphonenumber-js';
import Loading from 'flow/components/ui-components/Loading';
import { generatePath } from 'flow/utils/router-utils';

const EditCallRoutingAction = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { extensionKey, blocklistKey, type } = useParams<BlockistParams>();
    const callRouting = useAppSelector((state) =>
        state.callRoutings.getIn([type, blocklistKey]),
    );
    const selected = useAppSelector((state) =>
        state.callRoutings.getIn([type, blocklistKey, 'redirect']),
    );

    const [shouldShowForwardToNumber, setShouldShowForwardToNumber] =
        useState(false);
    const [referralNumber, setReferralNumber] = useState<ReferralNumberOption>({
        label: ['voicemail'].includes(selected)
            ? i18next.t('settings.number')
            : selected,
        dto: {},
    });

    useEffect(() => {
        if (selected !== undefined) {
            setShouldShowForwardToNumber(isValidPhoneNumber(selected));
        } else {
            setShouldShowForwardToNumber(false);
        }
    }, [selected]);

    useEffect(() => {
        dispatch(getBlockLists());
    }, []);

    const updateCallRoutingActionAndGoBack = (
        redirectOption?: ReferralNumberOption | string,
    ) => {
        let updatedCallRoutingDTO: any;
        updatedCallRoutingDTO = callRouting.update(
            'redirect',
            () => redirectOption || null,
        );

        dispatch(updateCallRouting(updatedCallRoutingDTO));

        history.push(
            generatePath(
                routes[
                    'settings.softphone.advanced.callrouting.type.blocklistKey'
                ],
                { extensionKey, type, blocklistKey },
            ),
        );
    };

    const handleReferralNumberChange = (number: ReferralNumberOption) => {
        if (!number) {
            return;
        }
        setReferralNumber(number);
        updateCallRoutingActionAndGoBack(number.dto.get('number'));
    };

    if (!callRouting) {
        return <Loading />;
    }

    return (
        <Page
            header={'media.settings.ringtone'}
            onClickBack={() =>
                history.push(
                    generatePath(
                        routes[
                            'settings.softphone.advanced.callrouting.type.blocklistKey'
                        ],
                        { extensionKey, type, blocklistKey },
                    ),
                )
            }
        >
            <RadioButton
                name={'settings.callrouting.action.playblock'}
                onChange={() => updateCallRoutingActionAndGoBack()}
                checked={!selected && shouldShowForwardToNumber}
            >
                {i18next.t('settings.callrouting.action.playblock')}
            </RadioButton>

            <RadioButton
                name={'settings.callrouting.action.tovoicemail'}
                onChange={() => updateCallRoutingActionAndGoBack('voicemail')}
                checked={selected === 'voicemail' && !shouldShowForwardToNumber}
            >
                {i18next.t('settings.callrouting.action.tovoicemail')}
            </RadioButton>

            <RadioButton
                name={'settings.callrouting.action.tonumber'}
                onChange={() => setShouldShowForwardToNumber(true)}
                checked={shouldShowForwardToNumber}
            >
                {i18next.t('settings.callrouting.action.tonumber')}
            </RadioButton>

            {shouldShowForwardToNumber && (
                <>
                    <SettingSection className="text-sm/4">
                        {i18next.t('settings.callrouting.action.picknumber')}
                        <span>&nbsp;*</span>
                    </SettingSection>
                    <ReferralNumberCombobox
                        ariaLabel={i18next.t(
                            'settings.callrouting.action.picknumber',
                        )}
                        referralNumber={referralNumber}
                        onChange={handleReferralNumberChange}
                    />
                </>
            )}
        </Page>
    );
};

export default EditCallRoutingAction;
